/* global CarouselSlider */

if (!customElements.get('product-card-image-slider')) {
  customElements.whenDefined('carousel-slider').then(() => {
    class ProductCardImageSlider extends CarouselSlider {
      constructor() {
        super();
        this.productCard = this.closest('product-card');
        this.productCardSwatches = this.productCard.querySelectorAll('.card__swatches .opt-btn');
        this.slides = Array.from(this.querySelectorAll('.slider__item'));
        this.counterCurrent = this.querySelector('.js-slider-counter-current');
        this.dots = this.querySelectorAll('.js-slider-dot');

        this.productCard.addEventListener('change', this.handleSwatchChange.bind(this));

        this.dots.forEach((dot) => {
          dot.addEventListener('click', this.handleDotClick.bind(this));
        });

        this.currentIndex = 0;
      }

      init() {
        super.init();

        this.slider.addEventListener('scroll', this.scrollInProgress.bind(this));

        // If swatches are enabled, mark the swatch anchors in the slideshow
        if (this.productCardSwatches) {
          const activeSlide = this.querySelector('.slider__item[aria-current]');
          const activeSwatchId = this.productCard.querySelector('.card__swatches .opt-btn:checked')?.dataset.mediaId || activeSlide?.dataset.mediaId;
          let activeSwatchSlide;

          if (activeSwatchId) {
            this.productCardSwatches.forEach((swatch) => {
              const swatchSlide = this.querySelector(`[data-media-id="${swatch.dataset.mediaId}"]`);
              if (swatchSlide) {
                swatchSlide.setAttribute('data-swatch-anchor', 'true');

                // Set the active set of slideshow images
                if (swatchSlide.dataset.mediaId === activeSwatchId) {
                  activeSlide.removeAttribute('aria-current');
                  swatchSlide.setAttribute('aria-current', 'true');
                  activeSwatchSlide = swatchSlide;
                }
              }
            });
          }

          if (activeSwatchSlide) this.handleSwatchChange(null, activeSwatchSlide.dataset.mediaId);
        } else {
          // Show the current slider item
          const activeSlide = this.querySelector('.slider__item[aria-current]');
          if (activeSlide) {
            this.handleSwatchChange(null, activeSlide.dataset.mediaId);
          } else {
            this.slider.slides[1]?.setAttribute('aria-current', 'true');
          }
        }

        const currentSlide = this.querySelector('.slider__item[aria-current]');
        this.currentIndex = currentSlide ? this.slides.indexOf(currentSlide) : 0;
        this.updateIndicators(this.currentIndex);
      }

      /**
       * Shortens the scroll-settle debounce so the counter/dots/disabled
       * state on native (touch/trackpad) scroll keep up with the drag,
       * instead of waiting on the base class's default 300ms.
       */
      addListeners() {
        super.addListeners();

        if (this.nav) {
          this.slider.removeEventListener('scroll', this.scrollHandler);
          this.scrollHandler = debounce(this.handleScroll.bind(this), 50);
          this.slider.addEventListener('scroll', this.scrollHandler);
        }
      }

      /**
       * Updates the visibility of slides based on the state of card swatches.
       * @param {string} mediaId - The media ID to match against the slides.
       */
      updateSlideVisibility(mediaId) {
        let hideSlide = true;
        let foundMediaId = false;
        this.slides.forEach((slide) => {
          if (!foundMediaId && slide.getAttribute('data-media-id') === mediaId) {
            hideSlide = false;
            foundMediaId = true;
          } else if (foundMediaId && slide.hasAttribute('data-swatch-anchor')) {
            hideSlide = true;
          }

          slide.toggleAttribute('hidden', hideSlide);
        });

        this.setButtonStates();
      }

      /**
       * Handles 'change' events in the product card swatches.
       * @param {object} evt - Event object.
       * @param {string} mediaId - The media ID to match against the slides.
       */
      handleSwatchChange(evt, mediaId) {
        const swatchMediaId = evt?.target.dataset.mediaId || mediaId;
        if (swatchMediaId) {
          this.updateSlideVisibility(swatchMediaId);

          const variantMedia = this.querySelector(`[data-media-id="${swatchMediaId}"]`);
          if (variantMedia) {
            const left = variantMedia.closest('.slider__item').offsetLeft;
            this.slider.scrollTo({ left, behavior: 'instant' });
          }
        }
      }

      /**
       * Sets the disabled state of the nav buttons.
       */
      setButtonStates() {
        if (!this.prevBtn && !this.nextBtn) {
          return;
        }

        const currentSlideIndex = Math.round(this.slider.scrollLeft / this.slideSpan) + 1;
        const visibleSlideCount = this.slides.filter((slide) => slide.hidden !== true).length;

        this.prevBtn.disabled = currentSlideIndex === 1
          || (this.getSlideVisibility(this.slides[0]) && this.slider.scrollLeft === 0);
        this.nextBtn.disabled = visibleSlideCount === currentSlideIndex
          || this.getSlideVisibility(this.slides[this.slides.length - 1]);
      }

      /**
       * Handles 'scroll' events on the slider element.
       */
      scrollInProgress() {
        this.slider.querySelector('[aria-current]')?.removeAttribute('aria-current');
      }

      /**
       * Handles 'scroll' events on the slider element.
       */
      handleScroll() {
        super.handleScroll();
        this.setActiveIndex(this.currentIndex);
      }

      /**
       * Handles 'click' events on the nav buttons container. Updates the
       * counter, dots and disabled state immediately instead of waiting
       * for the (debounced) scroll-settle handler to catch up. Ignored
       * while a previous click's scroll animation is still in progress,
       * so spamming the button can't queue up multiple jumps.
       * @param {object} evt - Event object.
       */
      handleNavClick(evt) {
        if (this.isAnimating) return;

        const btn = evt.target.closest('.slider-nav__btn');
        if (!btn || btn.disabled) return;

        super.handleNavClick(evt);

        const direction = btn.name === 'next' ? 1 : -1;
        const newIndex = Math.min(Math.max(this.currentIndex + direction, 0), this.slides.length - 1);
        this.setActiveIndex(newIndex);
        this.lockNavigation();
      }

      /**
       * Handles 'click' events on a dot indicator. Ignored while a
       * previous click's scroll animation is still in progress.
       * @param {object} evt - Event object.
       */
      handleDotClick(evt) {
        if (this.isAnimating) return;

        const index = Number(evt.currentTarget.dataset.index);
        const slide = this.slides[index];
        if (slide) {
          this.slider.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
          this.setActiveIndex(index);
          this.lockNavigation();
        }
      }

      /**
       * Briefly blocks further nav/dot clicks until the smooth-scroll
       * triggered by the current one has had time to settle.
       */
      lockNavigation() {
        this.isAnimating = true;
        clearTimeout(this.navigationLockTimeout);
        this.navigationLockTimeout = setTimeout(() => {
          this.isAnimating = false;
        }, 400);
      }

      /**
       * Sets the current slide index and immediately syncs aria-current,
       * the counter, the dots and the nav buttons' disabled state to it.
       * @param {number} index - Index of the current slide.
       */
      setActiveIndex(index) {
        this.currentIndex = index;
        this.slider.querySelector('[aria-current]')?.removeAttribute('aria-current');
        this.slides[index]?.setAttribute('aria-current', 'true');
        this.updateIndicators(index);

        if (this.prevBtn) this.prevBtn.disabled = index <= 0;
        if (this.nextBtn) this.nextBtn.disabled = index >= this.slides.length - 1;
      }

      /**
       * Updates the slide counter and active dot to match the current slide.
       * @param {number} index - Index of the current slide.
       */
      updateIndicators(index) {
        if (this.counterCurrent) this.counterCurrent.textContent = index + 1;

        this.dots.forEach((dot, dotIndex) => {
          dot.classList.toggle('tw:!bg-[#111111]', dotIndex === index);
          dot.classList.toggle('tw:!bg-[#d9d9d9]', dotIndex !== index);
        });
      }
    }

    customElements.define('product-card-image-slider', ProductCardImageSlider);
  });
}
